import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Plus, Settings, QrCode, ExternalLink, PowerOff, Power } from "lucide-react";
import { toast } from "sonner";

export default function Outlets() {
  const { user } = useAuth();
  const isSuper = user?.role === "super_admin";
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [subscriptionPlan, setSubscriptionPlan] = useState("TRIAL");
  const [loading, setLoading] = useState(false);
  const [confirmOutlet, setConfirmOutlet] = useState(null);
  const [confirmPassword, setConfirmPassword] = useState("");
  const [acting, setActing] = useState(false);

  const load = async () => {
    const { data } = await api.get("/outlets");
    setItems(data);
  };

  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/outlets", { name, subscription_plan: subscriptionPlan });
      const label = subscriptionPlan === "TRIAL" ? "Trial 14 hari" : subscriptionPlan;
      toast.success(`Outlet dibuat • ${label}`);
      setName(""); setSubscriptionPlan("TRIAL"); setOpen(false);
      await load();
    } catch (e2) {
      toast.error(formatApiError(e2.response?.data?.detail) || "Gagal membuat outlet");
    } finally { setLoading(false); }
  };

  const deactivate = async (e) => {
    e.preventDefault();
    if (!confirmOutlet) return;
    setActing(true);
    try {
      await api.patch(`/outlets/${confirmOutlet.id}/deactivate`, { password: confirmPassword });
      toast.success("Outlet dinonaktifkan");
      setConfirmOutlet(null); setConfirmPassword("");
      await load();
    } catch (e2) {
      toast.error(formatApiError(e2.response?.data?.detail) || "Gagal menonaktifkan outlet");
    } finally { setActing(false); }
  };

  const reactivate = async (o) => {
    try {
      await api.patch(`/outlets/${o.id}/reactivate`);
      toast.success("Outlet diaktifkan kembali");
      await load();
    } catch {
      toast.error("Gagal mengaktifkan outlet");
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight">Outlet</h1>
          <p className="text-sm text-zinc-500 mt-1">{isSuper ? "Kelola semua outlet dan pengaturannya." : "Outlet yang Anda kelola."}</p>
        </div>
        {isSuper && (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button className="bg-emerald-600 hover:bg-emerald-700" data-testid="new-outlet-button">
                <Plus size={16} className="mr-2" /> Outlet Baru
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>Buat Outlet Baru</DialogTitle></DialogHeader>
              <form onSubmit={create} className="space-y-4">
                <div className="space-y-1.5">
                  <Label htmlFor="outlet-name">Nama Bisnis</Label>
                  <Input id="outlet-name" value={name} onChange={e => setName(e.target.value)} required
                    data-testid="new-outlet-name-input" placeholder="Kedai Kopi ABC" />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="subscription-plan">Paket Awal</Label>
                  <select
                    id="subscription-plan"
                    value={subscriptionPlan}
                    onChange={e => setSubscriptionPlan(e.target.value)}
                    className="w-full h-10 rounded-md border border-zinc-300 bg-white px-3 text-sm"
                    data-testid="new-outlet-subscription-plan"
                  >
                    <option value="TRIAL">Trial 14 Hari</option>
                    <option value="BESTIE">BESTIE • Berbayar 30 Hari</option>
                    <option value="CARE">CARE • Berbayar 30 Hari</option>
                    <option value="SULTAN">SULTAN • Berbayar 30 Hari</option>
                  </select>
                  <p className="text-xs text-zinc-500">
                    Trial gratis 14 hari, atau langsung aktifkan paket berbayar.
                  </p>
                </div>
                <Button type="submit" disabled={loading} className="w-full bg-emerald-600 hover:bg-emerald-700"
                  data-testid="new-outlet-submit">
                  {loading ? "Menyimpan..." : "Simpan"}
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        )}
      </div>

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {items.map(o => (
          <div key={o.id} className="bg-white border border-zinc-200 rounded-2xl p-5 hover:shadow-md transition-shadow" data-testid={`outlet-card-${o.id}`}>
            <div className="flex items-center gap-3">
              {o.logo_url
                ? <img src={o.logo_url} alt={o.name} className="w-12 h-12 rounded-xl object-cover border border-zinc-200" />
                : <div className="w-12 h-12 rounded-xl grid place-items-center font-bold text-white" style={{ backgroundColor: o.brand_color || "#059669" }}>{o.name?.[0] || "O"}</div>}
              <div className="min-w-0 flex-1">
                <div className="font-semibold truncate">{o.name}</div>
                <div className="text-xs text-zinc-500 truncate">/{o.slug}</div>
              </div>
              {o.is_active === false && <Badge className="bg-zinc-200 text-zinc-600 border-0">Nonaktif</Badge>}
            </div>
            <div className="mt-4 flex flex-wrap gap-2">
              <Link to={`/admin/outlets/${o.id}/settings`}>
                <Button variant="outline" size="sm" data-testid={`outlet-settings-${o.id}`}>
                  <Settings size={14} className="mr-1.5" /> Pengaturan
                </Button>
              </Link>
              <Link to={`/admin/outlets/${o.id}/qr`}>
                <Button variant="outline" size="sm" data-testid={`outlet-qr-${o.id}`}>
                  <QrCode size={14} className="mr-1.5" /> QR
                </Button>
              </Link>
              <a href={`/f/${o.slug}`} target="_blank" rel="noreferrer">
                <Button variant="outline" size="sm" data-testid={`outlet-preview-${o.id}`}>
                  <ExternalLink size={14} className="mr-1.5" /> Buka
                </Button>
              </a>
              {isSuper && (o.is_active === false ? (
                <Button variant="outline" size="sm" className="text-emerald-700 border-emerald-300" onClick={() => reactivate(o)} data-testid={`outlet-reactivate-${o.id}`}>
                  <Power size={14} className="mr-1.5" /> Aktifkan
                </Button>
              ) : (
                <Button variant="outline" size="sm" className="text-red-700 border-red-300" onClick={() => setConfirmOutlet(o)} data-testid={`outlet-deactivate-${o.id}`}>
                  <PowerOff size={14} className="mr-1.5" /> Nonaktifkan
                </Button>
              ))}
            </div>
          </div>
        ))}
        {items.length === 0 && <div className="col-span-full text-center text-zinc-400 py-12">Belum ada outlet.</div>}
      </div>

      <Dialog open={!!confirmOutlet} onOpenChange={(v) => { if (!v) { setConfirmOutlet(null); setConfirmPassword(""); } }}>
        <DialogContent>
          <DialogHeader><DialogTitle>Nonaktifkan Outlet</DialogTitle></DialogHeader>
          <form onSubmit={deactivate} className="space-y-4">
            <p className="text-sm text-zinc-600">
              Outlet <span className="font-semibold">{confirmOutlet?.name}</span> akan berhenti menerima feedback baru.
              Data feedback dan pelanggan tetap tersimpan dan dapat diaktifkan kembali.
            </p>
            <div className="space-y-1.5">
              <Label htmlFor="confirm-password">Konfirmasi Password Super Admin</Label>
              <Input id="confirm-password" type="password" required value={confirmPassword}
                onChange={e => setConfirmPassword(e.target.value)} data-testid="deactivate-password-input" />
            </div>
            <Button type="submit" disabled={acting} variant="destructive" className="w-full" data-testid="deactivate-confirm-button">
              {acting ? "Memproses..." : "Nonaktifkan Outlet"}
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
